import { Icon, Stack, TableRow, Tooltip, Typography } from '@mui/material';
import {
  Assessment,
  DATE_FORMAT_SHORT_FNS,
  FilterKeys,
  GetAssessmentsQueryFilters,
  GetAssessmentsQuerySort,
  SORT_DIRECTION,
} from '@schooly/api';
import { RecurrenceInfo } from '@schooly/components/recurring';
import { AssessmentStatuses, DATE_FORMAT } from '@schooly/constants';
import { useAssessmentLabels } from '@schooly/hooks/use-assessment-labels';
import {
  GridCell,
  GridHead,
  ReportGeneratedIcon,
  ReportsIcon,
  SortableCell,
  TruncatedLinks,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format } from 'date-fns';
import { FC, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import useAppLocation from '../../hooks/useAppLocation';
import { AssessmentReportsTooltip } from './AssessmentReportsTooltip';

type AssessmentsHeaderProps = {
  sort?: GetAssessmentsQuerySort[];
  onChangeSort: (v: GetAssessmentsQuerySort[]) => void;
  chartIcon?: React.ReactNode;
};

export const AssessmentsHeader: FC<AssessmentsHeaderProps> = ({
  onChangeSort,
  sort,
  chartIcon,
}) => {
  const { formatMessage } = useIntl();
  const handleSort = (columnTextId: GetAssessmentsQuerySort['columnTextId']) => () => {
    const currentDirection = sort?.find((s) => s.columnTextId === columnTextId)?.direction;
    const newDirection =
      currentDirection === SORT_DIRECTION.ASC ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC;

    switch (columnTextId) {
      case 'evaluation_type':
        onChangeSort([
          { columnTextId: 'evaluation_type', direction: newDirection },
          { columnTextId: 'area_of_learning_name', direction: newDirection },
          { columnTextId: 'assessment_date', direction: SORT_DIRECTION.DESC },
          { columnTextId: 'name', direction: SORT_DIRECTION.ASC },
        ]);
        break;
      case 'assessment_date':
        onChangeSort([
          { columnTextId: 'assessment_date', direction: newDirection },
          { columnTextId: 'name', direction: SORT_DIRECTION.ASC },
          { columnTextId: 'evaluation_type', direction: SORT_DIRECTION.ASC },
          { columnTextId: 'area_of_learning_name', direction: SORT_DIRECTION.ASC },
        ]);
        break;
      case 'name':
        onChangeSort([
          { columnTextId: 'name', direction: newDirection },
          { columnTextId: 'evaluation_type', direction: SORT_DIRECTION.ASC },
          { columnTextId: 'area_of_learning_name', direction: SORT_DIRECTION.ASC },
          { columnTextId: 'assessment_date', direction: SORT_DIRECTION.DESC },
        ]);
        break;
      case 'assessment_status':
        onChangeSort([
          { columnTextId: 'assessment_status', direction: newDirection },
          { columnTextId: 'publish_date', direction: SORT_DIRECTION.DESC },
          { columnTextId: 'name', direction: SORT_DIRECTION.ASC },
          { columnTextId: 'evaluation_type', direction: SORT_DIRECTION.ASC },
          { columnTextId: 'area_of_learning_name', direction: SORT_DIRECTION.ASC },
          { columnTextId: 'assessment_date', direction: SORT_DIRECTION.DESC },
        ]);
        break;
      default:
        onChangeSort([{ columnTextId, direction: newDirection }]);
    }
  };

  return (
    <GridHead
      borderBottom
      sx={{
        '& .MuiTableCell-root:first-of-type': {
          pl: 2.5,
        },
        '& .MuiTableCell-root:last-of-type': {
          pr: 2.5,
        },
      }}
    >
      <SortableCell
        label={formatMessage({ id: 'assessments-Date' })}
        sort={sort?.[0]}
        columnTextId="assessment_date"
        onChangeSort={handleSort}
        width={80}
      />
      <SortableCell
        label={formatMessage({ id: 'assessments-Name' })}
        sort={sort?.[0]}
        columnTextId="name"
        onChangeSort={handleSort}
      />
      <SortableCell
        label={formatMessage({ id: 'assessments-AoLTutorsFeedback' })}
        sort={sort?.[0]}
        columnTextId="evaluation_type"
        onChangeSort={handleSort}
        width={160}
        sx={(theme) => ({
          [theme.breakpoints.only('lg')]: {
            width: 130,
          },
          [theme.breakpoints.only('md')]: {
            width: 90,
          },
          [theme.breakpoints.down('md')]: {
            width: 110,
          },
        })}
      />
      <GridCell
        width={200}
        sx={(theme) => ({
          [theme.breakpoints.only('lg')]: {
            width: 180,
          },
          [theme.breakpoints.only('md')]: {
            width: 140,
          },
          [theme.breakpoints.down('md')]: {
            width: 160,
          },
        })}
      >
        {formatMessage({ id: 'assessments-GroupsStudents' })}
      </GridCell>
      <GridCell
        width={200}
        sx={(theme) => ({
          [theme.breakpoints.only('lg')]: {
            width: 180,
          },
          [theme.breakpoints.only('md')]: {
            width: 140,
          },
          [theme.breakpoints.down('md')]: {
            width: 160,
          },
        })}
      >
        {formatMessage({ id: 'assessments-Type' })}
      </GridCell>
      <SortableCell
        label={formatMessage({ id: 'assessments-AssessmentStatus' })}
        sort={sort?.[0]}
        columnTextId="assessment_status"
        onChangeSort={handleSort}
        width={150}
        sx={(theme) => ({
          [theme.breakpoints.only('lg')]: {
            width: 130,
          },
          [theme.breakpoints.only('md')]: {
            width: 90,
          },
          [theme.breakpoints.down('md')]: {
            width: 110,
          },
        })}
      />
      <GridCell pr={1} right width={50}>
        {chartIcon}
      </GridCell>
    </GridHead>
  );
};

type AssessmentRowProps = {
  assessment: Assessment;
  onAddFilters: (v: GetAssessmentsQueryFilters) => void;
};

export const AssessmentRow: FC<AssessmentRowProps> = ({ assessment, onAddFilters }) => {
  const location = useAppLocation();
  const { getAssessmentEvaluationTitle } = useAssessmentLabels();

  const autoGeneratedFromReport = assessment.autogenerated;
  const hasReports = !!assessment.reports.length;
  const showReportIcon = autoGeneratedFromReport || hasReports;

  const recurrenceId = assessment.recurring_state?.recurrence_id;
  const recurringState = assessment.recurring_state;
  const showRecurrenceIcon = !!recurringState && !!recurrenceId;

  const evaluationTitle = getAssessmentEvaluationTitle(assessment);

  return (
    <TableRow
      component={Link}
      to={`/assessments/${assessment.id}`}
      state={{ backgroundLocation: location }}
      sx={(theme) => ({
        '&:hover td': {
          backgroundColor: theme.palette.background.default,
          '.MuiTypography-root': {
            '&:not(.noHoverText)': {
              color: theme.palette.primary.main,
            },
            '&.hoverTextSecondary': {
              color: theme.palette.common.grey2,
            },
          },
          '.MuiIcon-root': {
            color: theme.palette.primary.main,
          },
        },
      })}
    >
      <GridCell pl={2.5}>
        <Typography color="common.grey" className="hoverTextSecondary">
          {format(
            newDateTimezoneOffset(assessment.assessment_date),
            DATE_FORMAT_SHORT_FNS,
          ).toUpperCase()}
        </Typography>
      </GridCell>
      <GridCell>
        <Stack flexDirection="row" gap={1}>
          <TypographyWithOverflowHint variant="h3" color="common.grey2" noWrap>
            {assessment.name}
          </TypographyWithOverflowHint>
          {showRecurrenceIcon && (
            <RecurrenceInfo
              recurringState={recurringState}
              onClick={() =>
                onAddFilters({
                  [FilterKeys.RecurrenceId]: [recurrenceId],
                })
              }
            />
          )}
          {showReportIcon && (
            <AssessmentReportsTooltip assessment={assessment}>
              <Icon sx={{ color: 'common.grey2' }}>
                {assessment.autogenerated ? <ReportGeneratedIcon /> : <ReportsIcon />}
              </Icon>
            </AssessmentReportsTooltip>
          )}
        </Stack>
      </GridCell>
      <GridCell>
        <TypographyWithOverflowHint
          color={evaluationTitle ? 'common.grey2' : 'common.grey'}
          className={evaluationTitle ? undefined : 'hoverTextSecondary'}
        >
          {evaluationTitle ?? '-'}
        </TypographyWithOverflowHint>
      </GridCell>
      <GridCell>
        <GroupsCellContent assessment={assessment} />
      </GridCell>
      <GridCell>
        <MethodCellContent assessment={assessment} />
      </GridCell>
      <GridCell>
        <StatusCellContent assessment={assessment} />
      </GridCell>
      <GridCell />
    </TableRow>
  );
};

type StatusCellContentProps = {
  assessment: Assessment;
};

export const StatusCellContent: React.FC<StatusCellContentProps> = ({ assessment }) => {
  const { $t } = useIntl();

  const isPublished = assessment.status === AssessmentStatuses.Published;
  const isIncomplete = !isPublished && (!assessment?.methods.length || !assessment?.groups.length);

  const currentStatus = isIncomplete ? (
    $t({ id: 'status-Draft' })
  ) : isPublished ? (
    <>
      {$t({ id: 'status-Published' })}{' '}
      {assessment.publish_date
        ? format(newDateTimezoneOffset(assessment.publish_date), DATE_FORMAT)
        : ''}
    </>
  ) : (
    $t({ id: 'status-NotPublished' })
  );

  const getColor = () => {
    if (isPublished) return 'success.main';
    if (isIncomplete) return 'common.grey';
    return 'warning.main';
  };

  return (
    <Typography className={isIncomplete ? 'hoverTextSecondary' : 'noHoverText'} color={getColor()}>
      {currentStatus}
    </Typography>
  );
};

type CellContentProps = {
  assessment: Assessment;
};

export const MethodCellContent: React.FC<CellContentProps> = ({ assessment }) => {
  const { formatMessage } = useIntl();
  const method = assessment.methods[0];
  const ref = useRef<HTMLDivElement>(null);

  if (!method)
    return (
      <Typography color="common.grey" className="hoverTextSecondary">
        {'-'}
      </Typography>
    );

  const restMethodsLength = assessment.methods.length - 1;

  const content = (
    <Typography ref={ref} color="common.grey2">
      {formatMessage({ id: `assessments-Type-${method.method_type}` })}{' '}
      {!!restMethodsLength && `+${restMethodsLength}`}
    </Typography>
  );

  return (
    <>
      <Stack flexDirection="row">
        {!!restMethodsLength ? (
          <Tooltip
            disableInteractive
            placement="bottom-start"
            arrow
            title={
              <>
                {assessment.methods.map((m) => (
                  <Typography key={m.method_type}>
                    {formatMessage({ id: `assessments-Type-${m.method_type}` })}
                  </Typography>
                ))}
              </>
            }
          >
            {content}
          </Tooltip>
        ) : (
          content
        )}
      </Stack>
    </>
  );
};

export const GroupsCellContent: React.FC<CellContentProps> = ({ assessment: { groups } }) => {
  if (!groups.length)
    return (
      <Typography color="common.grey" className="hoverTextSecondary">
        {'-'}
      </Typography>
    );

  return (
    <TruncatedLinks
      items={groups.map((g) => ({
        name: g.name,
        link: { to: `/groups/${g.id}` },
      }))}
      typographyProps={{
        color: 'common.grey2',
        sx: { '&[href]:hover': { color: 'primary.main' } },
      }}
      tooltipProps={{
        arrow: false,
        placement: 'bottom-start',
        componentsProps: {
          tooltip: {
            sx: { px: 0.5, py: 0.25, minWidth: 300, maxHeight: 400, overflowY: 'scroll' },
          },
        },
        slotProps: {
          popper: {
            sx: (theme) => ({
              '&.MuiTooltip-popper[data-popper-placement*="bottom"] .MuiTooltip-tooltip': {
                margin: theme.spacing(-2.5, 0, 0, -0.5),
              },
            }),
          },
        },
      }}
    />
  );
};
